import { Link } from "react-router-dom";
import { useResource } from "@/lib/api";
import { PageHeader, Panel, Stat, StatusPill, Loading, ErrorNote, fmtTime } from "@/components/kit";

const CONN = [
  { key: "A", label: "Gateway operational", desc: "FastAPI, MongoDB, MCP and OAuth endpoints responding." },
  { key: "B", label: "OpenAI API Developer Console", desc: "Interim console. Not your ChatGPT conversation." },
  { key: "C", label: "Direct ChatGPT connection", desc: "Verified only after an authenticated ChatGPT tool call is audited." },
];

export default function Overview() {
  const { data, error, loading } = useResource("/overview");
  const conn = useResource("/connectors");
  if (loading) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  const states = { A: "operational", B: conn.data?.openai_console.status, C: data.chatgpt.status };
  return (
    <div>
      <PageHeader eyebrow="Autonomous Operations System · Phase 1" title="Operations Overview" />
      <div className="cc-rise mb-8 grid gap-px overflow-hidden rounded-sm border border-[var(--cc-navy)] bg-[var(--cc-navy)] md:grid-cols-3" data-testid="connection-states">
        {CONN.map((c) => (
          <Link to="/connectors" key={c.key} className="group bg-[var(--cc-navy)] p-6 text-white transition-colors hover:bg-[var(--cc-navy-2)]" data-testid={`connection-state-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className="font-display text-5xl font-extrabold text-[var(--cc-signal)]">{c.key}</span>
              {states[c.key] ? <StatusPill status={states[c.key]} /> : <span className="cc-pulse text-xs">…</span>}
            </div>
            <p className="mt-4 font-display text-lg font-bold">{c.label}</p>
            <p className="mt-1 text-xs text-[#A9BBD3]">{c.desc}</p>
          </Link>
        ))}
      </div>
      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Agents registered" value={data.counts.agents} hint={`${data.agents_active} active · all L0`} testId="stat-agents" />
        <Stat label="Pending approvals" value={data.pending_approvals} hint="Product Owner decision" testId="stat-approvals" />
        <Stat label="Audit records" value={data.counts.audit_log} hint={data.audit_chain.valid ? "Hash chain intact" : "CHAIN BROKEN"} testId="stat-audit" />
        <Stat label="Permission ceiling" value={data.permission_ceiling} hint="Observer · read-only" testId="stat-ceiling" />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Panel title="Recent audit evidence" className="min-w-0 lg:col-span-3" testId="recent-audit" aside={<Link to="/audit" className="text-xs text-[var(--cc-info)] hover:underline">All records →</Link>}>
          <ul className="divide-y divide-[var(--cc-line)]">
            {data.recent_audit.map((e) => (
              <li key={e.seq} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
                <span className="w-10 font-mono text-xs text-[var(--cc-muted)]">#{e.seq}</span>
                <span className="min-w-0 basis-40 flex-1 truncate"><b className="font-medium">{e.action}</b> <span className="text-[var(--cc-muted)]">by {e.actor}</span></span>
                <StatusPill status={e.outcome} />
                <span className="font-mono text-[11px] text-[var(--cc-muted)]">{fmtTime(e.timestamp)}</span>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Architectural laws" className="min-w-0 lg:col-span-2" testId="laws-panel">
          <ol className="space-y-3">
            {data.laws.map((l) => (
              <li key={l.id} className="flex gap-3 border-l-2 border-[var(--cc-signal)] pl-3">
                <span className="font-mono text-xs text-[var(--cc-muted)]">{l.id}</span>
                <span className="text-sm font-medium text-[var(--cc-navy)]">{l.name}</span>
              </li>
            ))}
          </ol>
          <div className="mt-6 space-y-1.5 text-xs text-[var(--cc-muted)]">
            <p>· Deny-by-default; unauthorized actions rejected server-side.</p>
            <p>· No agent can approve its own work.</p>
            <p>· ChatGPT instructions are never implicit Production authorization.</p>
            <p>· GitHub: <StatusPill status={data.github.status} /></p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
